import React, { useState, useEffect, useMemo } from 'react'
import { Users, Loader2 } from 'lucide-react'
import { OrgChart } from '@/components/OrgChart'
import { TeamMember } from '@/types/dashboard'
import { findPersonInHierarchy, type MonumentaEmployee } from '@/lib/real-data-service'

interface TeamOrgChartProps {
  isManager: boolean
  directReports: MonumentaEmployee[]
  user: MonumentaEmployee
  onMemberClick: (member: TeamMember) => void
}

export function TeamOrgChart({ isManager, directReports, user, onMemberClick }: TeamOrgChartProps) {
  const [hierarchyData, setHierarchyData] = useState<Map<string, number>>(new Map())
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Load hierarchy data for direct reports count
  useEffect(() => {
    const loadHierarchyData = async () => {
      if (!isManager || directReports.length === 0) {
        setIsLoading(false)
        return
      }

      try {
        setIsLoading(true)
        setError(null)

        const hierarchyMap = new Map<string, number>()

        // Load hierarchy data for each direct report
        await Promise.all(
          directReports.map(async (report) => {
            try {
              const reportHierarchyNode = await findPersonInHierarchy(report.name)
              const directReportsCount = reportHierarchyNode?.directReports?.length || 0
              hierarchyMap.set(report.name, directReportsCount)
            } catch (err) {
              console.warn(`Failed to load hierarchy for ${report.name}:`, err)
              hierarchyMap.set(report.name, 0) // Default to 0 if failed
            }
          })
        )

        setHierarchyData(hierarchyMap)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load hierarchy data')
        console.error('Error loading hierarchy data for TeamOrgChart:', err)
      } finally {
        setIsLoading(false)
      }
    }

    loadHierarchyData()
  }, [isManager, directReports])

  // Memoize the children data
  const childrenData = useMemo(() => {
    return directReports.map(report => ({
      id: report.id,
      name: report.name,
      role: report.role,
      level: 1,
      team: report.department,
      avatar: report.name.split(' ').map((n: string) => n[0]).join('').substring(0, 2),
      email: report.email,
      phone: '(11) 99999-0000',
      directReports: hierarchyData.get(report.name) || 0,
      parentId: user.id,
    }))
  }, [directReports, hierarchyData, user.id])

  return (
    <div className="card">
      <h3 className="font-display text-xl font-bold text-white text-center mb-6 bg-gradient-to-r from-white to-brand-gray-light bg-clip-text text-transparent">Organograma da Equipe</h3>
      <div className="py-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 text-brand-primary animate-spin mr-3" />
            <span className="text-brand-gray font-body">Carregando organograma...</span>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <p className="text-red-400 font-body text-lg mb-2">Erro ao carregar organograma</p>
            <p className="text-brand-gray text-sm font-body">{error}</p>
          </div>
        ) : isManager && directReports.length > 0 ? (
          <OrgChart
            member={{
              id: user.id,
              name: user.name,
              role: user.role,
              level: 0,
              team: user.department,
              avatar: user.name.split(' ').map((n: string) => n[0]).join('').substring(0, 2),
              email: user.email,
              phone: '(11) 99999-0000',
              directReports: directReports.length,
              parentId: undefined,
            }}
            children={childrenData}
            onMemberClick={onMemberClick}
          />
        ) : (
          <div className="text-center py-12">
            <Users className="w-16 h-16 text-brand-gray mx-auto mb-4" />
            <p className="text-brand-gray-light font-body text-lg">
              {isManager ? 'Nenhum subordinado encontrado' : 'Você não possui subordinados diretos'}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}